import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AnnotationService,
  onAddingAnnotationDataChanged,
  onAnnotationClickClosed,
  onAnnotationClickedIn,
  onAnnotationClickedOut,
  onAnnotationClickOpened,
  onAnnotationMarkerSelectedActiveHover,
  onAnnotationMarkerSelectedInActiveHover,
  onAnnotationsLoaded,
  onAudioPlayerInitialized,
  onCancelAddingAnnotation,
  onCommentsVisibilityChange,
  onFirstRowMarkersReady,
  onMediaDownloadClick,
  onAnnotationClickClosedV2,
  onAnnotationClickOpenedV2,
  onConversationPluginReady,
  onPluginReady,
} from '../../services/annotation.service';
import { AudioAnnotationPlugin } from './plugin/audio-annotation.plugin';
import { EmitterService } from '../../services/emitter.service';
import { commentFileAdd } from '../../services/comments.service';
import { wsListenerANNOTATION_CREATED, MtmStompService } from '../../services/mtm-stomp.service';
import {
  ActiveSubsectionState,
  AiOptions,
  aiParamsFromAnnotationEvent,
  AnnotationPrivateCondition,
  AnnotationSendMediaMessage,
  OptionInfo,
  QueryState,
} from '../../interfaces';
import { catchError, first, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { SubsectionService } from '../../services/subsection.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { OverlayService } from '../../services/overlayService';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProjectService } from '../../services/project.service';
import { AuthService } from '../../services/auth.service';
import { MTMFileDownloadControl } from '../mtm-file-download/mtm-file-download-control/mtm-file-download-control.component';
import { PermissionService } from '../../services/permissions.service';
import { checkInvalidUser } from '../../utils/permission-checks';
import { v4 as uuid4 } from 'uuid';
import { AudioAnnotationHandler } from '../audio-annotation-handler/audio-annotation-handler';
// import { CommentAnnotationPlugin } from "../comment-annotation/plugin/comment-annotation.plugin";
import { CommentAnnotationPlugin, Environment, PluginConfig } from '@mtm/conversation-client-sdk-annotation';
import { HttpClient } from '@angular/common/http';
import { PlatformRbacSignatureService, SignatureRequestResult } from '../../services/platform-rbac-signature.service';
import { environment } from '../../../../environments/environment';
import WaveSurfer from 'wavesurfer.js';
import { TopMediaInfoService } from '../top-media-info/top-media-info.service';
import { MediaPlayerStoreService } from '../../services/media-player-store.service';
import { currentAppToken } from '../../auth/app-token';

@Component({
  selector: 'mtm-audio-annotation',
  styleUrl: './audio-annotation.component.scss',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './audio-annotation.component.html',
  standalone: false,
})
export class AudioAnnotation implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('downloadButton') downloadButton: MTMFileDownloadControl;
  private readonly subsectionService = inject(SubsectionService);
  private readonly permissionService = inject(PermissionService);
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  private readonly httpClient = inject(HttpClient);
  private readonly rbacSignature = inject(PlatformRbacSignatureService);
  private readonly mpStoreService = inject(MediaPlayerStoreService);
  private readonly ngZone = inject(NgZone);

  /** When the control bar is narrower than this (px), overflow actions go into the "more" menu. */
  readonly controlBarMoreDropdownBreakpointPx = 600;

  controlBarIsNarrow = false;

  private controlBarResizeObserver: ResizeObserver | null = null;

  @ViewChild('controlBar') set controlBar(ref: ElementRef<HTMLElement> | undefined) {
    if (this.controlBarResizeObserver) {
      this.controlBarResizeObserver.disconnect();
      this.controlBarResizeObserver = null;
    }
    const el = ref?.nativeElement;
    if (!el) {
      return;
    }
    const applyWidth = (): void => {
      const w = el.getBoundingClientRect().width;
      const narrow = w > 0 && w < this.controlBarMoreDropdownBreakpointPx;
      this.ngZone.run(() => {
        if (this.controlBarIsNarrow !== narrow) {
          this.controlBarIsNarrow = narrow;
        }
      });
    };
    this.controlBarResizeObserver = new ResizeObserver(() => {
      applyWidth();
    });
    this.controlBarResizeObserver.observe(el);
    applyWidth();
  }

  @Input() projectId: any;
  @Input() sectionId: any;
  @Input() participants: any[] = [];
  @Input() subsectionId: any;
  @Input() typeId: any;
  @Input() item: any;
  @Input() annotations: any[];
  @Input() authUser: any;
  @Input() hideAnnotationToolbar: boolean = true;
  @Input() enableAudioAnnotation: boolean = false;
  @Input() hideTopMediaInfo: boolean = false;
  @Input() hasEditPermission: boolean = false;
  @Input() topMediaInfoTemplate: TemplateRef<any>;
  @Input() assetListTemplate: TemplateRef<any>;
  @Input() assetInfoTemplate: TemplateRef<any>;
  @Input() reviewLink: any;
  @Input() shareLinkPreview: boolean = false;
  @Input() instanceId: string;
  @ViewChild('elapsedMask') elapsedMask: ElementRef;
  @ViewChild('selectableRange') selectableRange: ElementRef;
  @ViewChild('remainingMask') remainingMask: ElementRef;
  @ViewChild('waveformContainer') waveformContainer!: ElementRef;
  @ViewChild('postWaveform') postWaveform: ElementRef;

  amplitudeReady: boolean = false;
  playerReady: boolean = false;
  progressBarClickedTime: number | null = null;
  audioDuration: any;
  audioStep: any;
  elapsedWidth: any;
  annotationPlugin: CommentAnnotationPlugin;
  pluginReady: boolean;
  // annotatingMode: boolean = false;
  annotationComment: any = '';
  isAnnotationDisplayEnabled: boolean = true;
  // activeAnnotation: any;
  startTime: any;
  endTime: any;
  isTimeFormatOpen: boolean = false;
  isBottomTimeFormatOpen: boolean = false;
  activeTimeFormat: string = 'seconds';
  volume: any = 0.5;
  volumeMute: boolean = false;
  sampleRate: number = 250;
  isWaveformCreated: boolean = false;
  waveformPath: any;
  maskWaveform: any;
  wavesurfer: WaveSurfer;
  activeTimeMode: string;
  markerAvatarSize: number = 28;
  prevVolume: number;
  muteClick: boolean = false;
  progressBarMarkers: any;
  wavesurferPre: WaveSurfer;
  wavesurferPost: WaveSurfer;
  preWaveFormReady: boolean = false;
  postWaveFormReady: boolean = false;
  waveFormsDisplay: boolean = false;
  isPlaying: boolean = false; //Handled in plugin
  tempTime: any;
  loopRange: { start: number; end: number } | null = null;
  userInfo: any;
  annotationToolbarActive: boolean = true;
  wsId: string;
  newFiles: any = [];
  privateCondition: AnnotationPrivateCondition = null;

  files: any = [];
  videoRecordFiles: any = [];
  audioRecordFiles: any = [];
  ngUnsubscribe = new Subject();
  isBusy: boolean = false;
  subSectionState: ActiveSubsectionState;
  isAssetListVisible: boolean;
  optionsState: QueryState<OptionInfo>;
  audioHandler: AudioAnnotationHandler;

  private resizeObserver!: ResizeObserver;
  resizeDebounceTime = 500; // in ms

  generalCommentingMode: boolean;
  hasComment: boolean = false;
  isShareLinkPreview: boolean = false;
  isReleasePreview: boolean = false;
  isCompareMode: boolean = false;
  private compareViewerFullscreen = false;
  signature: any;
  currentPlaybackRate: number = 1;

  get isFullscreen() {
    return this.isCompareMode ? this.compareViewerFullscreen : this.topMediaInfoService.isFullscreen;
  }

  /** Viewport fullscreen layout (SCSS); :host-context/:has() is unreliable with encapsulation None. */
  @HostBinding('class.audio-viewer-fullscreen-host')
  get audioViewerFullscreenHost(): boolean {
    return this.isFullscreen;
  }

  get isAdding() {
    return this.annotationPlugin?.controls?.uiState?.adding && this.annotationPlugin?.controls?.uiState?.drawingEnabled;
  }

  get annotatingMode() {
    return this.isAdding || false;
  }

  get annotationData() {
    return this.annotationPlugin?.annotationState?.annotationMessages || [];
  }

  get editingMode(): boolean {
    return this.annotationPlugin?.controls?.uiState?.editing || false;
  }

  get activeAnnotation() {
    return this.annotationPlugin?.annotationState?.activeAnnotation || null;
  }

  get currentTime(): number {
    return this.wavesurfer?.getCurrentTime() || 0;
  }

  get playedPercent(): string {
    const duration: number = Number(this.audioDuration) || 0;
    if (!duration) return '0%';
    const pct: number = Math.max(0, Math.min(100, (this.currentTime / duration) * 100));
    return `${pct}%`;
  }

  constructor(
    private readonly stompService: MtmStompService,
    private readonly annotationService: AnnotationService,
    private readonly authService: AuthService
  ) {}

  ngAfterViewInit(): void {
    this.initAudio();
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    this.wsId = this.stompService.subscribeToService();
    onCommentsVisibilityChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.updateMarker(args),
    });
    // onAddingAnnotationDataChanged.pipe(
    // 	takeUntil(this.ngUnsubscribe)
    // ).subscribe({
    // 	next: (args: any) => this.updateTimeRange(args)
    // })
    onCancelAddingAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onCancelAddingAnnotationHandler(args),
    });
    onFirstRowMarkersReady.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.setFirstRowMarkers(args),
    });
    onAnnotationMarkerSelectedActiveHover.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.activateWaveforms(args),
    });
    onAnnotationMarkerSelectedInActiveHover.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.deactivateWaveforms(args),
    });
    onAnnotationClickedIn.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.activateWaveforms(args),
    });
    onAnnotationClickedOut.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.deactivateWaveforms(args),
    });
    onAnnotationClickClosedV2.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => {
        if (args.instanceId !== this.instanceId || args.itemId !== this.item?.id) return;
        this.loopRange = null;
        this.deactivateWaveforms(args);
      },
    });
    onMediaDownloadClick.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => {
        if (args.instanceId != this.instanceId || args.itemId != this.item?.id) {
          return;
        }
        this.downloadAsset();
      },
    });
    // this.stompService.subscribeToListener(
    //   'ANNOTATION_CREATED',
    //   this.wsId,
    //   [this.projectId, this.sectionId, this.subsectionId],
    //   (args: any) => this.wsOnAnnotationCreate(args),
    //   wsListenerANNOTATION_CREATED
    // );

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Check if the current entry's target is the resizeDiv element
        if (entry.target === this.waveformContainer.nativeElement && this.annotationPlugin && this.pluginReady) {
          const { width } = entry.contentRect;
          const { height } = entry.contentRect;
        }
      }
    });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev?.commentsCollapsed === curr?.commentsCollapsed)
      )
      .subscribe({
        next: (state) => {
          this.hasComment = state?.commentsCollapsed;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isShareLinkPreview === curr.isShareLinkPreview)
      )
      .subscribe({
        next: (state) => {
          this.isShareLinkPreview = state.isShareLinkPreview;
        },
      });
    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isReleasePreview === curr.isReleasePreview)
      )
      .subscribe({
        next: (state) => {
          this.isReleasePreview = state.isReleasePreview;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.compareMode === curr.compareMode)
      )
      .subscribe({
        next: (state) => {
          const nextCompare = state?.compareMode ?? false;
          if (!nextCompare) {
            this.compareViewerFullscreen = false;
          }
          this.isCompareMode = nextCompare;
        },
      });

    this.resizeObserver.observe(this.waveformContainer.nativeElement);

    EmitterService.get(AnnotationSendMediaMessage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data) => {
          if (this.instanceId != data.instanceId) {
            return;
          }
          if (data.message) {
            this.annotationComment = data.message;
            this.newFiles = data.newFiles || [];
            const aiParams = aiParamsFromAnnotationEvent(data);
            if (data.type === 'edit') {
              if (this.editingMode) {
                this.saveEditedAnnotation(aiParams);
              } else {
                this.annotationPlugin.fire('updateComment', {
                  annotationId: data.comment.annotation.id,
                  commentId: data.comment.comment.id,
                  body: data.message,
                  files: data.newFiles,
                  agentVersionId: data.agentVersionId,
                  mediaQuality: data.mediaQuality,
                });
              }
            } else {
              this.addNewAnnotation(aiParams);
            }

            this.isBusy = false;
          }
        },
      });
  }

  saveEditedAnnotation(aiParams: AiOptions) {
    if (this.annotationPlugin && this.editingMode && this.pluginReady) {
      this.audioHandler.saveEditedAnnotation(this.annotationComment, aiParams);
    }
  }

  ngOnInit(): void {
    this.authUser = this.authService.getAuthUser();
    const participantList = this.participants ?? [];
    this.userInfo = participantList.find((participant) => participant?.username === this.authUser?.username) as any;
    this.annotationService.privateConditionChange$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (value: AnnotationPrivateCondition) => {
        this.privateCondition = value;
      },
    });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged(
          (prev, curr) =>
            prev.activeOption?.optionId === curr.activeOption?.optionId &&
            prev.activeVersion?.versionNumber === curr.activeVersion?.versionNumber
        )
      )
      .subscribe({
        next: (state) => {
          this.subSectionState = state;
        },
      });

    this.subsectionService.options$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (options) => {
        this.optionsState = options;
      },
    });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.activeFile === curr.activeFile)
      )
      .subscribe({
        next: (state) => {
          this.subSectionState = state;
        },
      });

    this.extendWaveformRenderer();
  }

  extendWaveformRenderer() {
    const ws = WaveSurfer.create({ container: '#temp' });
    const { renderer } = ws as any;

    // Extend the renderSingleCanvas method
    const originalRenderSingleCanvas = renderer.constructor.prototype.renderSingleCanvas;
    renderer.constructor.prototype.renderSingleCanvas = function (
      data,
      options,
      width,
      height,
      offset,
      canvasContainer,
      progressContainer
    ) {
      // Call original for waveform canvas
      const pixelRatio = this.getPixelRatio();
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.style.left = `${Math.round(offset)}px`;
      canvasContainer.appendChild(canvas);

      const ctx = canvas.getContext('2d');

      if (options.renderFunction) {
        ctx.fillStyle = this.convertColorValues(options.waveColor);
        options.renderFunction(data, ctx);
      } else {
        this.renderWaveform(data, options, ctx);
      }

      // Custom progress rendering
      if (canvas.width > 0 && canvas.height > 0) {
        const progressCanvas = canvas.cloneNode();
        const progressCtx = (progressCanvas as any).getContext('2d');

        // Re-render with progressColor
        if (options.renderFunction) {
          progressCtx.fillStyle = this.convertColorValues(options.progressColor);
          options.renderFunction(data, progressCtx);
        } else {
          const progressOptions = { ...options, waveColor: options.progressColor };
          this.renderWaveform(data, progressOptions, progressCtx);
        }

        progressContainer.appendChild(progressCanvas);
      }
    };
    ws.destroy();
  }

  ngOnDestroy(): void {
    $(document).off('keydown');
    if (this.controlBarResizeObserver) {
      this.controlBarResizeObserver.disconnect();
      this.controlBarResizeObserver = null;
    }
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
    this.stompService.unsubscribeToService(this.wsId);
  }

  get playerState() {
    return this.wavesurfer?.isPlaying();
  }

  checkInvalidUser(): boolean {
    return checkInvalidUser(this.permissionService, this.reviewLink);
  }

  wsOnAnnotationCreate(args) {
    commentFileAdd.emit({
      projectId: this.projectId,
      sectionId: this.sectionId,
      subSectionId: this.subsectionId,
      comment: args.mediaAnnotation.comments[0],
      annotationId: args.mediaAnnotation.id,
      files: this.newFiles,
    });
    this.cleanUpFiles();
  }

  onPlayerInit() {
    this.audioDuration = this.wavesurfer.getDuration();
    this.audioStep = 0.01;
    const config: PluginConfig = {
      annotationObjects: this.annotations,
      item: this.item,
      userInfo: this.authUser,
      entityName: 'FILE',
      instanceId: this.instanceId,
      itemSignature: this.signature.access_token,
      httpClient: this.httpClient,
      participants: this.prepareParticipantList(),
      environment: environment.environmentType as Environment,
      ...(this.shareLinkPreview ? { isPrivate: false } : {}),
    };
    this.playerReady = true;
    this.wavesurfer.setVolume(this.volume);
    this.annotationPlugin = new CommentAnnotationPlugin(config);
    this.annotationPlugin.onReady(() => {
      this.audioHandler = new AudioAnnotationHandler(this.wavesurfer, this.annotationPlugin);
      onPluginReady.emit({
        annotationPlugin: this.annotationPlugin,
        itemId: this.item.id,
        instanceId: this.instanceId,
        type: 'audio',
      });
      onConversationPluginReady.emit({
        annotationPlugin: this.annotationPlugin,
        instanceId: this.instanceId,
        itemId: this.item?.id,
      });
      this.pluginReady = true;
      this.bindCloseAnnotationKey();
      this.updateMarkerDimensions();
    });
  }

  updateAudioTime(time) {
    const t = Number(time);
    if (typeof t === 'number' && !Number.isNaN(t)) {
      this.progressBarClickedTime = t;
      setTimeout(() => (this.progressBarClickedTime = null), 0);
    }
    this.wavesurfer.setTime(time);
  }

  audioPausePlay() {
    this.wavesurfer.playPause();
  }

  inputClickChange() {
    if (this.annotationPlugin) {
      this.annotationPlugin.fire('timeUpdate');
    }
  }

  updateMarker(args) {
    if (args.itemId == this.item.id && args.instanceId == this.instanceId && this.pluginReady) {
      this.annotations = args.data;
    }
    if (this.activeAnnotation?.data) {
      this.activeAnnotation.data = args.data.find((item) => item.id === this.activeAnnotation?.data?.id);
      if (args.previewAnnotation && this.activeAnnotation.data) {
        args.previewAnnotation = this.activeAnnotation.data;
      }
    }
    this.updateMarkerDimensions();
  }

  updateMarkerDimensions() {
    if (!this.annotationPlugin) {
      return;
    }
    this.annotationPlugin.annotationState.annotationMessages.forEach((annotation: any) => {
      annotation.left = this.getPercentage(annotation.range.start);
      annotation.width = this.getPercentage(annotation.range.end - annotation.range.start);
      const leftWidth = annotation.left;
      const { width } = annotation;
      const rigthWidth = 100 - (leftWidth + width);
      if (leftWidth > rigthWidth) {
        annotation.markerPos = 'left';
      } else if (leftWidth <= rigthWidth) {
        annotation.markerPos = 'right';
      }
      annotation.markerLeftSet = false;
    });
  }

  getPercentage(val) {
    return (val / this.wavesurfer.getDuration()) * 100;
  }

  // hoverInAnnotation(annotation) {
  //   if (
  //     this.annotationPlugin &&
  //     this.pluginReady &&
  //     (!this.activeAnnotation || this.activeAnnotation.type === 'hover')
  //   ) {
  //     this.annotationPlugin.fire('hoverInAnnotation', { annotation: annotation });
  //   }
  // }
  // hoverOutAnnotation(annotation) {
  //   if (
  //     this.annotationPlugin &&
  //     this.pluginReady &&
  //     (!this.activeAnnotation || this.activeAnnotation.type === 'hover')
  //   ) {
  //     this.annotationPlugin.fire('hoverOutAnnotation', { annotation: annotation });
  //   }
  // }

  annotationBarClick(annotation, event) {
    if (event.classList.contains('close-marker')) return;
    const _this2 = this;
    if (this.annotationPlugin && this.pluginReady && this.preWaveFormReady && this.postWaveFormReady) {
      if (
        this.activeAnnotation &&
        annotation.id === this.activeAnnotation?.data.id &&
        this.activeAnnotation.type === 'click'
      ) {
        this.annotationPlugin.fire('clickOutAnnotation');
      } else {
        this.annotationPlugin.fire('clickInAnnotation', { annotation: annotation });
      }
    }
  }

  closeActiveAnnotation() {
    if (this.annotationPlugin && this.pluginReady && this.preWaveFormReady && this.postWaveFormReady) {
      if (this.activeAnnotation?.type === 'click') {
        this.annotationPlugin.fire('clickOutAnnotation');
      }
    }
  }

  bindCloseAnnotationKey() {
    const _this2 = this;
    $(document).on('keydown', (e) => {
      const ev = e as unknown as KeyboardEvent;
      if (ev.key === 'Escape' && this.isFullscreen) {
        ev.preventDefault();
        this.ngZone.run(() => {
          _this2.toggleFullscreenForViewer();
        });
        return;
      }
      if (ev.key === 'Escape' && this.activeAnnotation?.type === 'click') {
        _this2.closeActiveAnnotation();
      }
    });
  }

  generateSignature() {
    return this.rbacSignature
      .requestSignature('FILE', this.item.id, this.item.displayName)
      .pipe(catchError(() => of<SignatureRequestResult | null>(null)));
  }

  prepareParticipantList() {
    const inputParticipants = this.participants || [];
    const normalizedAuthEmail = (this.authUser?.email || '').toLowerCase();

    const hasAuthUser = inputParticipants.some((p) => {
      const email = (p?.email || p?.accountEmail || '').toLowerCase();
      return email && normalizedAuthEmail && email === normalizedAuthEmail;
    });

    const mapped = inputParticipants.map((participant) => ({
      accountEmail: participant?.email || participant?.accountEmail,
      owner: (participant?.email || participant?.accountEmail) === this.authUser?.email,
    }));

    if (!hasAuthUser && normalizedAuthEmail) {
      mapped.push({
        accountEmail: this.authUser.email,
        owner: true,
      });
    }

    return mapped;
  }

  initialiseTemplateWave() {
    const _this2 = this;

    // Export peaks with explicit structure
    const peaksData = this.wavesurfer.exportPeaks();
    const decodedData = this.wavesurfer.getDecodedData();

    const rawPeaks = [];
    for (let i = 0; i < decodedData.numberOfChannels; i++) {
      rawPeaks.push(decodedData.getChannelData(i));
    }

    const sharedConfig = {
      barWidth: this.wavesurfer.options.barWidth,
      barGap: this.wavesurfer.options.barGap,
      height: this.wavesurfer.options.height,
      normalize: this.wavesurfer.options.normalize,
      minPxPerSec: this.wavesurfer.options.minPxPerSec,
    };
    const token = currentAppToken();
    this.wavesurferPre = WaveSurfer.create({
      container: '#waveform2-' + this.instanceId,
      waveColor: 'transparent',
      progressColor: '#3d3939',
      ...sharedConfig,
    });

    // Use load with peaks and duration for exact match
    this.wavesurferPre.load(this.item.signedURL, rawPeaks, this.wavesurfer.getDuration());

    this.wavesurferPre.on('ready', () => {
      _this2.preWaveFormReady = true;
      (this.wavesurferPre as any).renderer.progress;
    });

    this.wavesurferPost = WaveSurfer.create({
      container: '#waveform3-' + this.instanceId,
      waveColor: '#3d3939',
      progressColor: 'transparent',
      ...sharedConfig,
    });

    this.wavesurferPost.load(this.item.signedURL, rawPeaks, this.wavesurfer.getDuration());

    this.wavesurferPost.on('ready', () => {
      _this2.postWaveFormReady = true;
    });
  }

  private saveWavesurferToMpStore(): void {
    if (!this.instanceId || !this.wavesurfer) {
      return;
    }
    this.mpStoreService.getStore(this.instanceId)?.updateStore({ player: this.wavesurfer });
  }

  initAudio() {
    const _this2 = this;
    const token = currentAppToken();
    this.wavesurfer = WaveSurfer.create({
      container: '#waveform-' + this.instanceId,
      waveColor: '#909091',
      progressColor: '#fe3b00',
      barWidth: 1,
      barGap: 1,
      cursorWidth: 2,
      cursorColor: '#FFFFFF',
      height: 200,
      fetchParams: {
        headers: [['Authorization', 'Bearer ' + token]],
      },
    });
    this.wavesurfer.load(this.item.signedURL);
    this.wavesurfer.once('ready', () => {
      _this2.saveWavesurferToMpStore();
      _this2.initialiseTemplateWave();
      _this2.generateSignature().subscribe((result) => {
        _this2.signature = result?.signature ?? null;
        _this2.onPlayerInit();
      });
      onAudioPlayerInitialized.emit({ act: 'audioPlayerInitialized' });
    });
    // TODO : Loop range handler based on requirements, current event no longer supported
    // in new version
    this.wavesurfer.on('audioprocess', () => {
      if (this.loopRange) {
        const { start } = this.loopRange;
        const endRaw = this.loopRange.end;
        const end = endRaw <= start ? start + 0.05 : endRaw;
        const now = this.wavesurfer.getCurrentTime();
        if (now >= end - 0.005) {
          this.wavesurfer.setTime(start);
        }
        if (now < start) {
          this.wavesurfer.setTime(start);
        }
      }
    });

    EmitterService.get('audioAnnotation.ready').emit();
  }

  startAnnotationMode() {
    const canAnnotate = !this.annotatingMode && this.annotationPlugin && this.pluginReady;

    if (canAnnotate && this.annotationToolbarActive) {
      this.annotationPlugin.fire('addingAnnotation');
    } else if (canAnnotate && !this.annotationToolbarActive) {
      this.generalCommentingMode = true;
    }
  }

  canceladdNewAnnotation() {
    if (this.annotationPlugin && this.annotatingMode && this.pluginReady) {
      this.annotationPlugin.fire('cancelAddingAnnotation');
    }
    if (this.generalCommentingMode) {
      this.onCancelAddingAnnotationHandler({ itemId: this.item.id, instanceId: this.instanceId });
    }
  }

  addNewAnnotation(aiParams: AiOptions) {
    if ((!this.annotationComment && !this.newFiles?.length) || this.isBusy) {
      return;
    }
    this.isBusy = true;
    if (this.isInValidInput()) return;

    if (this.newFiles.length && !this.annotationComment) {
      this.annotationComment = ' ';
    }
    if (this.annotationPlugin && this.annotatingMode && this.pluginReady) {
      // this.annotationPlugin.fire('saveAnnotation');
      this.audioHandler.saveAnnotation(this.annotationComment, aiParams, false);
    } else if (this.annotationPlugin && !this.annotatingMode && this.pluginReady) {
      // this.annotationPlugin.fire('saveGeneralComment');
      this.audioHandler.saveAnnotation(this.annotationComment, aiParams, true);
      this.generalCommentingMode = false;
    }
  }

  onCancelAddingAnnotationHandler(args?: any) {
    if (args.itemId != this.item.id || args.instanceId != this.instanceId) return;
    this.annotationComment = '';
    // this.annotatingMode = false;
    this.isBusy = false;
    this.generalCommentingMode = false;
    this.deactivateWaveforms({ itemId: this.item.id, instanceId: this.instanceId });
  }

  isInValidInput() {
    return !this.annotationComment && !this.newFiles.length;
  }

  updateTimeRange(args) {
    if (
      this.annotationPlugin &&
      this.pluginReady &&
      this.annotatingMode === true &&
      args.itemId === this.item.id &&
      args.instanceId === this.instanceId
    ) {
      this.startTime = args.data.range.start;
      this.endTime = args.data.range.end;
    }
  }

  // Time Range Section //

  getStartTime() {
    if (!this.wavesurfer) {
      return;
    }
    if (this.annotatingMode === false) {
      this.startTime = this.wavesurfer.getCurrentTime();
    } else {
      this.startTime = this.startTime;
    }

    return this.getTimeFormat(this.startTime);
  }

  getEndTime() {
    if (this.annotatingMode === false) {
      this.endTime = this.startTime;
    } else {
      this.endTime = this.endTime;
    }
    if (this.endTime === this.startTime) {
      if (this.activeTimeFormat === 'seconds') {
        return '--:--';
      } else if (this.activeTimeFormat === 'milliseconds') {
        return '--:--:---';
      }
    }

    return this.getTimeFormat(this.endTime);
  }

  getTimeFormat(time) {
    if (this.annotatingMode === false) {
      if (this.activeTimeFormat === 'seconds') {
        return '--:--';
      } else if (this.activeTimeFormat === 'milliseconds') {
        return '--:--:---';
      }
    }
    if (this.activeTimeFormat === 'seconds') {
      return new Date(time * 1000).toISOString().substr(14, 5);
    } else if (this.activeTimeFormat === 'milliseconds') {
      return new Date(time * 1000).toISOString().substr(14, 9).replace('.', ':');
    }
  }

  getCurrentTime() {
    const currentTime = this.wavesurfer.getCurrentTime();
    if (this.activeTimeFormat === 'seconds') {
      return new Date(currentTime * 1000).toISOString().substr(14, 5);
    } else if (this.activeTimeFormat === 'milliseconds') {
      return new Date(currentTime * 1000).toISOString().substr(14, 9).replace('.', ':');
    }
  }

  getAudioDuration() {
    if (!this.audioDuration) return;
    if (this.activeTimeFormat === 'seconds') {
      return new Date(this.audioDuration * 1000).toISOString().substr(14, 5);
    } else if (this.activeTimeFormat === 'milliseconds') {
      return new Date(this.audioDuration * 1000).toISOString().substr(14, 9).replace('.', ':');
    }
  }

  changeTimeFormat(format, location) {
    this.activeTimeFormat = format;
    if (location === 'top') {
      if (this.isBottomTimeFormatOpen) this.isBottomTimeFormatOpen = false;
    } else if (location === 'bottom') {
      if (this.isTimeFormatOpen) this.isTimeFormatOpen = false;
    }
  }

  changeActiveTimeMode(mode) {
    this.activeTimeMode = mode;
  }

  onStartTimeRangeChange(act: string) {
    if (this.activeTimeMode !== 'start') {
      this.activeTimeMode = 'start';
    }

    if (this.annotationPlugin && this.pluginReady && this.annotatingMode === true) {
      this.annotationPlugin.fire('startRangeUpdate', { act: act });
    }
  }

  onEndTimeRangeChange(act: string) {
    if (this.activeTimeMode !== 'end') {
      this.activeTimeMode = 'end';
    }

    if (this.annotationPlugin && this.pluginReady && this.annotatingMode === true) {
      this.annotationPlugin.fire('endRangeUpdate', { act: act });
    }
  }

  // Volume Section //

  updateVolume(value) {
    this.volume = value;
    this.wavesurfer.setVolume(this.volume);
  }

  toggleMute() {
    const { volume } = this;
    if (this.volume == 0 && !this.muteClick) {
      this.updateVolume(20);
      return;
    }
    if (this.volume > 0) {
      this.updateVolume(0);
      this.muteClick = true;
    } else if (this.volume == 0) {
      this.updateVolume(this.prevVolume);
      this.muteClick = false;
    }
    this.prevVolume = volume;
  }

  public onCinemaModeExitFullscreen(): void {
    this.toggleFullscreenForViewer();
  }

  public toggleFullscreenForViewer(): void {
    if (this.subsectionService?.uiState.cinemaMode) {
      this.subsectionService.updateUIState({ cinemaMode: false, explorerCollapsed: false });
    }
    if (this.isCompareMode) {
      this.compareViewerFullscreen = !this.compareViewerFullscreen;
      EmitterService.get('MEDIA_PLAYER:FULLSCREEN').emit();
      return;
    }
    document.body.classList.toggle('media-preview-fullscreen');
    document.body.classList.toggle('dashboard-fullscreen');
    if (this.isReleasePreview) {
      document.body.classList.toggle('release-fullscreen');
    }
    if (this.shareLinkPreview) {
      document.body.classList.toggle('share-link-fullscreen');
    }
    this.topMediaInfoService.toggleFullscreen();
    EmitterService.get('MEDIA_PLAYER:FULLSCREEN').emit();
  }

  // To set the width of bottom time control elements
  setTimeWidth(el) {
    const cs = getComputedStyle(el);
    return el.offsetWidth - (parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight));
  }

  setTimeHeight(el) {}

  // Update marker comment box left position
  updateMarkerBoxLeft(annotation, el) {
    const offsetMarkerWidth = 5;

    const containerModal = this.waveformContainer.nativeElement.getBoundingClientRect();
    const markerBoxModal = el.getBoundingClientRect();

    const width = markerBoxModal?.width;
    let left;
    if (annotation.markerPos === 'left') {
      left = -width - offsetMarkerWidth * 2 + 'px';
    } else if (annotation.markerPos === 'right') {
      left = el.parentElement.offsetWidth + offsetMarkerWidth + 'px';
    }

    return left + 42;
  }

  getAvatarLeft(annotation) {
    const parentWidth = this.waveformContainer.nativeElement.offsetWidth;
    let offSet = this.markerAvatarSize / 2;
    if (annotation.markerElement.offsetWidth < 20) {
      offSet = 7;
    }
    const avatarWidthOffset = (100 * offSet) / parentWidth;
    return (annotation.left + annotation.left + annotation.width) / 2 - avatarWidthOffset;
  }

  // TOGGLE ANNOTATION DISPLAY SECTION
  toggleAnnotationDisplay() {
    if (this.annotationPlugin && this.pluginReady) {
      const disabling = this.isAnnotationDisplayEnabled;
      this.isAnnotationDisplayEnabled = !this.isAnnotationDisplayEnabled;

      if (disabling) {
        // Close any click-open annotation so the plugin state stays consistent
        if (this.activeAnnotation?.type === 'click') {
          this.annotationPlugin.fire('annotationClickOut', {
            itemId: this.item.id,
            instanceId: this.instanceId,
            messageId: this.activeAnnotation.data.id,
          });
        }
        // Clear loop range and restore waveform to its default appearance
        this.loopRange = null;
        this.deactivateWaveforms({ itemId: this.item.id, instanceId: this.instanceId });
      } else {
        // clickIn events can fire while display is off, so activeAnnotation may already be set;
        // activate waveforms immediately to reflect the current annotation state
        if (this.activeAnnotation) {
          this.activateWaveforms({
            itemId: this.item.id,
            instanceId: this.instanceId,
            annotation: this.activeAnnotation.data,
            data: this.activeAnnotation,
          });
        }
      }
    }
  }

  downloadAsset() {
    this.downloadButton.downloadFile();
  }

  // PROGRESS MARKER SECTION
  setFirstRowMarkers(args) {
    if (args.itemId != this.item.id || args.instanceId != this.instanceId) return;
    this.progressBarMarkers = args.firstRowAnnotation;
    if (!args.firstRowAnnotation) return;
    this.progressBarMarkers.forEach((annotation) => {
      annotation.left = this.getPercentage(annotation.range.start);
      annotation.width = this.getPercentage(annotation.range.end - annotation.range.start);
    });
  }

  // MARKER WAVEFORM HANDLE SECTION
  activateWaveforms(args: any) {
    // Skip waveform activation when annotation display is toggled off
    if (!this.isAnnotationDisplayEnabled) return;
    const annotationId = args?.annotation?.id ?? args?.data?.annotation?.id;
    const annotation = this.findAnnotation(annotationId) as any;
    if (args?.itemId != this.item.id || args?.instanceId != this.instanceId || !annotation) return;

    this.wavesurferPre.setTime(annotation.range.start);
    this.wavesurferPost.setTime(annotation.range.end);

    const annotationEventType = args?.data?.type || 'hover';
    if (annotationEventType === 'hover') {
      this.tempTime = this.wavesurfer.getCurrentTime();
      this.wavesurfer.setTime(annotation.range.start);
    } else if (annotationEventType === 'click') {
      this.tempTime = null;
    }

    let postWidth = annotation.left + annotation.width;
    if (!postWidth) {
      this.updateMarkerDimensions();
      postWidth = annotation.left + annotation.width;
    }

    // this.postWaveform.nativeElement.style.right = -postWidth + "%";
    // this.postWaveform.nativeElement.firstElementChild.style.left = -postWidth + "%";

    this.waveFormsDisplay = true;
    this.wavesurfer.setOptions({
      waveColor: 'white',
    });
  }

  deactivateWaveforms(args: any) {
    if (args?.itemId != this.item.id || args?.instanceId != this.instanceId) return;
    if (!(this.tempTime == null)) {
      this.wavesurfer.setTime(this.tempTime);
      this.tempTime = null;
    }

    this.waveFormsDisplay = false;
    this.wavesurfer.setOptions({
      waveColor: '#909091',
    });
  }

  // Returns annotation object given ID
  findAnnotation(id) {
    if (this.annotationPlugin) {
      return this.annotationPlugin.annotationState.annotationMessages.find((annotation: any) => annotation.id === id);
    }
    return null;
  }

  /** Gets the comment input place holder text depending on general or annot comment */
  getPlaceholderText() {
    let placeholderText;
    if (this.annotationPlugin && this.annotationPlugin.controls && (this.isAdding || this.annotationToolbarActive)) {
      placeholderText = 'Write an annotation comment...';
    } else {
      placeholderText = 'Write a general comment...';
    }

    return placeholderText;
  }

  /** Toggle annotation toolbar display on/off using toggle */
  toggleAnnotationToolbarDisplay(annotationToolbarActive) {
    if (!this.annotationPlugin) {
      return;
    }
    if (annotationToolbarActive == true) {
      this.annotationPlugin.fire('togglePiklorColors', true); // Changes colors in palette to depict greyed out
    } else {
      this.canceladdNewAnnotation(); // Triggers canceladdnew event on the plugin
      this.annotationPlugin.fire('togglePiklorColors', false);
    }
  }

  cleanUpFiles() {
    this.newFiles = [];
    this.files = [];
    this.videoRecordFiles = [];
    this.audioRecordFiles = [];
  }

  uploadFileCallback(fileObject) {
    const { files } = fileObject;
    if (!files) {
      return;
    }
    files.map((file) => {
      if (!file.fileLocalId) {
        file.fileLocalId = uuid4();
      }
      return file;
    });
    files.forEach((file) => {
      if (file.method == 'audioRecord') {
        this.audioRecordFiles.push(file);
      } else if (file.method == 'videoRecord') {
        this.videoRecordFiles.push(file);
      } else {
        this.files.push(file);
      }
    });
    this.newFiles.push(...files);
  }

  onFileDeleted(file) {
    this.newFiles = this.newFiles.filter((f) => f.lastModified != file.lastModified);
    this.audioRecordFiles = this.newFiles.filter((f) => f.method == 'audioRecord');
    this.videoRecordFiles = this.newFiles.filter((f) => f.method == 'videoRecord');
    this.files = this.newFiles.filter((f) => !f.method);
  }

  handlePrivateConditionChange(value: AnnotationPrivateCondition) {
    this.privateCondition = value;
    this.annotationService.updatePrivateCondition(value);
  }

  timeDisplayFormat: 'Seconds' | 'Frames' = 'Seconds';
  progressTooltipVisible: boolean = false;
  progressTooltipX: number = 0;
  progressTooltipY: number = 0;
  progressTooltipText: string = '';

  public onTimeDisplayFormatChange(format: 'Seconds' | 'Frames'): void {
    this.timeDisplayFormat = format;
  }

  public onProgressBarMouseMove(event: MouseEvent): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const duration = Number(this.audioDuration) || 0;
    if (!duration) return;
    const time: number = fraction * duration;
    this.progressTooltipX = event.clientX;
    this.progressTooltipY = event.clientY;
    this.progressTooltipText = this.formatProgressTooltip(time);
    this.progressTooltipVisible = true;
  }

  public onProgressBarMouseLeave(): void {
    this.progressTooltipVisible = false;
  }

  private formatProgressTooltip(seconds: number): string {
    if (this.timeDisplayFormat === 'Frames') {
      return Math.floor(seconds * 25).toString();
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const mStr = `${m}`.padStart(h > 0 ? 2 : 1, '0');
    const sStr = `${s}`.padStart(2, '0');
    return h > 0 ? `${h}:${mStr}:${sStr}` : `${mStr}:${sStr}`;
  }

  public onReject(): void {}

  public onApprove(): void {}

  public onCompare(): void {}

  public onInfo(): void {}

  public onSettingsChange(speed: number): void {
    this.currentPlaybackRate = speed;
    this.wavesurfer.setPlaybackRate(this.currentPlaybackRate);
  }

  public onCinemaMode(): void {}

  public resetImageOrientation(): void {}
}
